<template>
  <div>
    <slot v-bind:password="generate()" />
  </div>
</template>

<script>
/**
 * Generate a random password
 *
 * There is no real view to this.
 * Just wrap whatever you need and pass the slot property information down
 * to whatever component needs it
 *
 * Used primarily on the User page
 */
import { arrayShuffle, arrayRandomItem } from '@/util/Helpers';

export default {
  name: 'PasswordGenerator',
  props: {
    length: {
      type: Number,
      default: 24,
    },
    lowercase: {
      type: [Boolean, Number],
      default: true,
      validator(v) {
        return typeof v === 'boolean' || Math.sign(v) !== -1;
      },
    },
    uppercase: {
      type: [Boolean, Number],
      default: true,
      validator(v) {
        return typeof v === 'boolean' || Math.sign(v) !== -1;
      },
    },
    numeric: {
      type: [Boolean, Number],
      default: true,
      validator(v) {
        return typeof v === 'boolean' || Math.sign(v) !== -1;
      },
    },
    special: {
      type: [Boolean, Number],
      default: false,
      validator(v) {
        return typeof v === 'boolean' || Math.sign(v) !== -1;
      },
    },
    whitespace: {
      type: [Boolean, Number],
      default: false,
      validator(v) {
        return typeof v === 'boolean' || Math.sign(v) !== -1;
      },
    },
  },
  data() {
    return {
      charsets: {
        lowercase: 'abcdefghijklmnopqrstuvwxyz'.split(''),
        uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
        numeric: '1234567890'.split(''),
        special: '`-=[]\\;\',./~!@#$%^&*()_+{}|:"<>?'.split(''),
      },
    };
  },
  computed: {
    totals() {
      const charProps = [
        this.lowercase,
        this.uppercase,
        this.numeric,
        this.special,
        this.whitespace,
      ];
      const explicit = charProps
        .reduce((total, v) => (typeof total === 'boolean' ? 0 : total) + (typeof v === 'boolean' ? 0 : v));
      const autoCount = charProps.filter((v) => v === true).length;
      return {
        explicit,
        auto: Math.floor((this.length - explicit) / (autoCount || 1)),
      };
    },
    charsetCounts() {
      const calc = (v) => {
        if (typeof v === 'boolean') {
          return v ? this.totals.auto : 0;
        }
        return v;
      };
      return {
        lowercase: calc(this.lowercase),
        uppercase: calc(this.uppercase),
        numeric: calc(this.numeric),
        special: calc(this.special),
        whitespace: calc(this.whitespace),
      };
    },
  },
  created() {
    if (this.totals.explicit > this.length) {
      throw new Error('Explicit character lengths may not exceed the total password length.');
    }
  },
  methods: {
    generate() {
      const generated = [];
      for (let i = 0; i < this.charsetCounts.lowercase; i += 1) {
        generated.push(arrayRandomItem(this.charsets.lowercase));
      }
      for (let i = 0; i < this.charsetCounts.uppercase; i += 1) {
        generated.push(arrayRandomItem(this.charsets.uppercase));
      }
      for (let i = 0; i < this.charsetCounts.numeric; i += 1) {
        generated.push(arrayRandomItem(this.charsets.numeric));
      }
      for (let i = 0; i < this.charsetCounts.special; i += 1) {
        generated.push(arrayRandomItem(this.charsets.special));
      }
      for (let i = 0; i < this.charsetCounts.whitespace; i += 1) {
        generated.push(' ');
      }
      const final = arrayShuffle(generated).join('');
      this.$emit('generated', final);
      return final;
    },
  },
};
</script>
